import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { format, isValid, parseISO } from "date-fns";
import { Observable, map, BehaviorSubject } from "rxjs";
import { SourceData } from "src/app/features/epoc/epoc.interface";
import { environment } from "../../../../environment";
import { EpocData } from "../../features/epoc/epoc.interface";
import { EmptyData } from "../constants/core-constants";
import { CPTCodes, PatientMediacalDiagnosis, TreatmentDiagnosis } from "../interfaces/patient-diagnosis";
import { PatientDocument } from "../interfaces/patient-document.interface";
import { DocumentDeletionConfirmationResponse } from "../interfaces/DocumentDeletionConfirmationResponse ";

@Injectable({ providedIn: "root" })
export class PatientDocumentClient {
  readonly endpoint = "patientdocumentation";

  // Medical Diagnosis
  public newMedicalDiagnosis: any[] = [];
  private modifiedMedicalList: PatientMediacalDiagnosis[] = [];
  private addMedicalDiagnoses$ = new BehaviorSubject<any>(null);
  private deleteMedicalDiagnoses$ = new BehaviorSubject<any>(null);
  private updatedMedicalDiagnosis$ = new BehaviorSubject<any>(null);
  addMedicalDiagnosisData = this.addMedicalDiagnoses$.asObservable();
  deleteMedicalDiagnosisData = this.deleteMedicalDiagnoses$.asObservable();
  updatedMedicalDiagnosisData = this.updatedMedicalDiagnosis$.asObservable();

  // Treatment Diagnosis  
  public newTreatmentDiagnosis: any[] = [];
  private modifiedTreatmentList: TreatmentDiagnosis[] = [];
  private addTreatmentDiagnoses$ = new BehaviorSubject<any>(null);
  private deleteTreatmentDiagnosis$ = new BehaviorSubject<any>(null);
  private updatedTreatmentDiagnosis$ = new BehaviorSubject<any>(null);
  addTreatmentDiagnosisData = this.addTreatmentDiagnoses$.asObservable();
  deletereatmentDiagnosisData = this.deleteTreatmentDiagnosis$.asObservable();
  updatedTreatmentDiagnosisData = this.updatedTreatmentDiagnosis$.asObservable();

  // CPT Codes
  public newCptCodes: any[] = [];
  private modifiedCPTcodes: CPTCodes[] = [];
  private selectedCPTCodes$ = new BehaviorSubject<any>(null);
  private deleteCPTCodes$ = new BehaviorSubject<any>(null);
  private updatedCPTCodes$ = new BehaviorSubject<any>(null);
  addCptCodesRowsData = this.selectedCPTCodes$.asObservable();
  deleteCptCodesRowsData = this.deleteCPTCodes$.asObservable();
  updatedCPTCodesData = this.updatedCPTCodes$.asObservable();

// Trigger update on EvaleDate and certification form data
  changeInEvalandCertification$ = new BehaviorSubject<any>(null);
  updatedEvalDate_CertificationDate = this.changeInEvalandCertification$.asObservable();

  //store touched fields
  private touchedSections$ = new BehaviorSubject<any>(null);
  touchedSections = this.touchedSections$.asObservable();

  public defaultPractionerId$ = new BehaviorSubject<any>(null);
  public defaultPractionerId = this.defaultPractionerId$.asObservable();

  touchedObject: any = {};

  isFunctionalLimitationsChanged : boolean = false;
  isUnderlyingImpairmentsChanged : boolean = false;
 
  constructor(private httpClient: HttpClient) { }

  emitTouchedFields(sectionId: string) {
    this.touchedSections$.next(sectionId);
  }

  /**
   * Emit Add Diagnosis Data
   * @param addDiagnosisData 
   * @param isUpdate 
   */
  emitAddDiagnosisData(addDiagnosisData: any, isUpdate?: boolean) {
    if (this.newMedicalDiagnosis.length == 0) {
      this.newMedicalDiagnosis = [...addDiagnosisData];
      this.addMedicalDiagnoses$.next(this.newMedicalDiagnosis);
    } else {
      addDiagnosisData.forEach((item: any) => {
        const findIndex = this.newMedicalDiagnosis.findIndex(ele => ele.icdId == item.icdId);
        if (findIndex == -1) {
          this.newMedicalDiagnosis.push(item);
        } else if (isUpdate) {
          this.newMedicalDiagnosis[findIndex] = item;
        }
      });
      this.addMedicalDiagnoses$.next(this.newMedicalDiagnosis);
    }
  }

  /**
   * Delete Medical Diagnosis 
   * @param deletedItem 
   * @param isExistingItem 
   */
  deleteNewMedicalDiagnosis(deletedItem: any, isExistingItem?: boolean) {
    if (this.newMedicalDiagnosis?.length && deletedItem?.icdId) {
      this.newMedicalDiagnosis = this.newMedicalDiagnosis.filter((item) => item.icdId !== deletedItem?.icdId);
      this.addMedicalDiagnoses$.next(this.newMedicalDiagnosis);
    }
    if (isExistingItem) {
      this.deleteMedicalDiagnoses$.next(deletedItem);
    }
  }
  /**
   * For Existing Medical diagnosis data changes
   * @param modifiedMedicalList 
   */
  updatedMedicalDiagnosis(modifiedMedicalList: PatientMediacalDiagnosis[]) {
    if (this.modifiedMedicalList.length == 0) {
      this.modifiedMedicalList = modifiedMedicalList;
      this.updatedMedicalDiagnosis$.next(this.modifiedMedicalList);
    } else {
      modifiedMedicalList.forEach((item: any) => {
        const findIndex = this.modifiedMedicalList.findIndex(ele => ele.icdId == item.icdId);
        if (findIndex == -1) {
          this.modifiedMedicalList.push(item);
        } else {
          this.modifiedMedicalList[findIndex] = item;
        }
      });
      this.updatedMedicalDiagnosis$.next(this.modifiedMedicalList);
    }
  }
  /**
   * clear Add Diagnosis emited Data
   */
  clearDiagnosisData() {
    this.addMedicalDiagnoses$.next(null);
    this.deleteMedicalDiagnoses$.next(null);
    this.updatedMedicalDiagnosis$.next(null);
    this.newMedicalDiagnosis = [];
    this.modifiedMedicalList = [];
  }

  /**
   * Emit Add Diagnosis Data
   * @param diagnosisData 
   */
  emitAddTreatmentDiagnosisData(diagnosisData: any, isUpdate?: boolean) {

    if (this.newTreatmentDiagnosis.length == 0) {
      this.newTreatmentDiagnosis = [...diagnosisData];
      this.addTreatmentDiagnoses$.next(this.newTreatmentDiagnosis);
    } else {
      diagnosisData.forEach((item: any) => {
        const findIndex = this.newTreatmentDiagnosis.findIndex(ele => ele.icdId == item.icdId);
        if (findIndex == -1) {
          this.newTreatmentDiagnosis.push(item);
        } else if (isUpdate) {
          this.newTreatmentDiagnosis[findIndex] = item;
        }
      });
      this.addTreatmentDiagnoses$.next(this.newTreatmentDiagnosis);
    }
  }

  /**
   * For Existing Treatment diagnosis data changes
   * @param modifiedTreatmentList 
   */
  updatedTreatmentDiagnosis(modifiedTreatmentList: TreatmentDiagnosis[]) {
    if (this.modifiedTreatmentList.length == 0) {
      this.modifiedTreatmentList = modifiedTreatmentList;
      this.updatedTreatmentDiagnosis$.next(this.modifiedTreatmentList);
    } else {
      modifiedTreatmentList.forEach((item: any) => {
        const findIndex = this.modifiedTreatmentList.findIndex(ele => ele.treatmentCodeId == item.treatmentCodeId);
        if (findIndex == -1) {
          this.modifiedTreatmentList.push(item);
        } else {
          this.modifiedTreatmentList[findIndex] = item;
        }
      });
      this.updatedTreatmentDiagnosis$.next(this.modifiedTreatmentList);
    }
  }

  /**
   * deleteTreatmentDiagnosis
   */
  public deleteTreatmentDiagnosis(deletedItem: any, isExistingItem?: boolean) {
    if (this.newTreatmentDiagnosis?.length && deletedItem?.icdId) {
      this.newTreatmentDiagnosis = this.newTreatmentDiagnosis.filter((item) => item.icdId !== deletedItem?.icdId);
      this.addTreatmentDiagnoses$.next(this.newTreatmentDiagnosis);
    }
    if (isExistingItem) {
      this.deleteTreatmentDiagnosis$.next(deletedItem);
    }
  }
  /**
   * clear Add Diagnosis emited Data
   */
  clearTreatmentDiagnosisData() {
    this.addTreatmentDiagnoses$.next(null);
    this.deleteTreatmentDiagnosis$.next(null);
    this.newTreatmentDiagnosis = [];
    this.updatedTreatmentDiagnosis$.next(null);
    this.modifiedTreatmentList = [];
  }

  clearTouchedObjectData(){
    this.touchedObject = {};
    this.isUnderlyingImpairmentsChanged = false;
    this.isFunctionalLimitationsChanged = false;
  }

  emitAddCPTCodesData(cptCodesData: any, isUpdate?: boolean) {
    if (this.newCptCodes.length == 0) {
      this.newCptCodes = [...cptCodesData];
      this.selectedCPTCodes$.next(this.newCptCodes);
    } else {
      cptCodesData.forEach((item: any) => {
        const findIndex = this.newCptCodes.findIndex(ele => ele.cptId == item.cptId);
        if (findIndex == -1) {
          this.newCptCodes.push(item);
        } else if (isUpdate) {
          this.newCptCodes[findIndex] = item;
        }
      });
      this.selectedCPTCodes$.next(this.newCptCodes);
    }
  }


  /**
 * For Existing CPT codes data changes
 * @param modifiedCPTList 
 */
  updatedCPTCodes(modifiedCPTList: CPTCodes[]) {
    if (this.modifiedCPTcodes.length == 0) {
      this.modifiedCPTcodes = modifiedCPTList;
      this.updatedCPTCodes$.next(this.modifiedCPTcodes);
    } else {
      modifiedCPTList.forEach((item: any) => {
        const findIndex = this.modifiedCPTcodes.findIndex(ele => ele.cptId == item.cptId);
        if (findIndex == -1) {
          this.modifiedCPTcodes.push(item);
        } else {
          this.modifiedCPTcodes[findIndex] = item;
        }
      });
      this.updatedCPTCodes$.next(this.modifiedCPTcodes);
    }
  }

  /**
   * deleteCPT
   */
  public deleteCPTCodesData(deletedItem: any, isExistingItem?: boolean) {
    if (this.newCptCodes?.length && deletedItem?.cptId) {
      this.newCptCodes = this.newCptCodes.filter((item) => item.cptId !== deletedItem?.cptId);
      this.selectedCPTCodes$.next(this.newCptCodes);
    }
    if (isExistingItem) {
      this.deleteCPTCodes$.next(deletedItem);
    }
  }

  clearAddCptCodes() {
    this.selectedCPTCodes$.next(null);
    this.deleteCPTCodes$.next(null);
    this.updatedCPTCodes$.next(null);
    this.newCptCodes = []
    this.modifiedCPTcodes = [];
  }

  /**
   * getPatientDocuments
   * @param patientId 
   * @param facilityId 
   * @param patientTherapyId 
   * @param paitentFacilityId 
   * @returns 
   */

  getPatientDocuments(
    patientId: string,
    facilityId: string,
    patientTherapyId: string,
    paitentFacilityId?: number
  ): Observable<PatientDocument[]> {
    return this.httpClient.post<PatientDocument[]>(
      `${environment.endpoint}/${this.endpoint}/get-patient-documents`,
      {
        patientid: patientId,
        facilityid: facilityId,
        patienttherapyid: patientTherapyId,
        patientfacilityid: paitentFacilityId,
      }
    );
  }
  getDeletedPatientDocuments(
    patientTherapyId: string
  ): Observable<PatientDocument[]> {
    return this.httpClient.get<PatientDocument[]>(
      `${environment.endpoint}/${this.endpoint}/get-all-deleted-documents`,
      {
        params: {
          patienttherapyid: patientTherapyId.toString(),
        }
      }
    );
  }

  checkSequentialRules(patientDocumentId: number): Observable<boolean> {
    return this.httpClient.get<boolean>(
      `${environment.endpoint}/${this.endpoint}/check-sequential-rules`,
      { 
        params: {
         patientDocumentId : patientDocumentId.toString(), 
        }
      }
    )
  }

  restoreDeleteDocument(
    patientDocumentId: string
  ): Observable<any> {
    return this.httpClient
      .post<{ responseData: SourceData[] }>(
        `${environment.endpoint}/${this.endpoint}/restore-deleted-document`, patientDocumentId
      )
      ?.pipe(map((response) => response.responseData));
  }
  getDefaultPractitionerId(patientDocumentId: number): Observable<any> {
    return this.httpClient.get<any>(
      `${environment.endpoint}/${this.endpoint}/get-default-practitioner`,
      {
        params: {
          patientDocumentId: patientDocumentId.toString(),
        },
      }
    );
  }

  getFile(patientDocumentId: number): Observable<Blob | any> {
    return this.httpClient
      .post(
        `${environment.endpoint}/${this.endpoint}/download-pdf`,
        {
          patientDocumentId: patientDocumentId.toString(),
        },
        {
          responseType: 'blob', // Default response type is blob
          observe: 'response', // This allows you to access the entire HTTP response
        }
      )
      .pipe(
        map((response) => {
          const contentType = response.headers.get('Content-Type');
          if (contentType && contentType.includes('application/json')) {
            // Handle JSON error response
            const reader = new FileReader();
            return new Observable((observer) => {
              reader.onload = () => {
                const jsonResponse = JSON.parse(reader.result as string);
                observer.error(jsonResponse); // Pass the error to the subscriber
              };
              reader.readAsText(response.body as Blob); // Convert Blob to JSON
            });
          }
          // If not JSON, treat as Blob
          return response.body;
        })
      );
  }
  

  getDocumentSectionHistory(
    sectionId: string,
    therapyId: number
  ): Observable<SourceData[]> {
    return this.httpClient
      .post<{ responseData: SourceData[] }>(
        `${environment.endpoint}/${this.endpoint}/GetDocumentSectionHistory`,
        {
          sectionId: sectionId,
          patientTherapyId: therapyId?.toString(),
        }
      )
      ?.pipe(map((response) => response.responseData));
  }
  copyHistoryToDocument(
    sectionId: string,
    currentPatientDocumentId: number,
    historyPatientDocumentId: number
  ): Observable<any> {
    return this.httpClient
      .post<{ responseData: SourceData[] }>(
        `${environment.endpoint}/${this.endpoint}/CopyHistoryToDocument`,
        {
          sectionId: sectionId,
          currentPatientDocumentId: currentPatientDocumentId,
          historyPatientDocumentId: historyPatientDocumentId
        }
      )
      ?.pipe(map((response) => response.responseData));
  }

  getPatientDocument(
    facilityId: string,
    patientDocumentId: number,
    therapyId: string
  ): Observable<{ documentResponse: EpocData; facilityDocumentId: number, deletedDocuments: string, isNarrativeMandatory: boolean}> {
    return this.httpClient
      .post<{
        responseData: {
          deletedDocuments: string;
          documentResponse: string;
          facilityDocumentId: number;
          isNarrativeMandatory: boolean;
        };
      }>(`${environment.endpoint}/${this.endpoint}/get-patient-document`, {
        facilityId: facilityId,
        patientDocumentId: patientDocumentId,
        patientTherapyId: therapyId,
      })
      .pipe(
        map((response) => ({
          documentResponse: JSON.parse(
            response.responseData.documentResponse
          ) as EpocData,
          facilityDocumentId: response.responseData.facilityDocumentId,
          deletedDocuments: response.responseData.deletedDocuments,
          isNarrativeMandatory: response.responseData.isNarrativeMandatory
        }))
      );
  }

  saveDocumentResponse(
    facilityId: string,
    patientTherapyId: number,
    patientDocumentId: number,
    evaluationChange: boolean,
    documentJson: string,
    isTempSave: boolean,
    isDocumentSubmitted: boolean,
    isAssistant : boolean = false,
    sendForCosign : boolean = false,
    cosignerid : string = "",
    resetSkilledTherapyProvided : boolean = false,
  ): Observable<EpocData> {
    const payload: any = {
      facilityId: facilityId,
      patientTherapyId: patientTherapyId,
      patientDocumentId: patientDocumentId,
      documentJson: documentJson,
      isTempSave: isTempSave,
      isDocumentSubmitted: isDocumentSubmitted,
      isAssistant: isAssistant,
      sendForCosign: sendForCosign,
      cosignerid : cosignerid,
      resetSkilledTherapyProvided : resetSkilledTherapyProvided,
      IsEvalChanged: evaluationChange
    };

    // if (documetFacilityId) {
    //   payload["documentFacilityId"] = documetFacilityId;
    // }

    return this.httpClient
      .post<{ responseData: EpocData }>(
        `${environment.endpoint}/${this.endpoint}/save-patient-response`,
        payload
      )
      .pipe(map((response) => response.responseData));
  }

  saveTransitionalDocumentResponse(
    facilityId: string,
    patientTherapyId: number,
    patientDocumentId: number,
    documentJson: string,
    isTempSave: boolean,
    isDocumentSubmitted: boolean,
    evaluationChange: boolean
  ): Observable<EpocData> {
    const payload: any = {
      facilityId: facilityId,
      patientTherapyId: patientTherapyId,
      patientDocumentId: patientDocumentId,
      IsEvalChanged: evaluationChange,
      documentJson: documentJson,
      isTempSave: isTempSave,
      isDocumentSubmitted: isDocumentSubmitted,
      cosignerid : ""
    };

    return this.httpClient
      .post<{ responseData: EpocData }>(
        `${environment.endpoint}/${this.endpoint}/save-patient-response-as-transitionalComplete`,
        payload
      )
      .pipe(map((response) => response.responseData));
  }

  getSummaryInfo(patientTherapyId: number) {
    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/get-placeholder-info-for-summary`,
        {
          params: {
            patientTherapyId: patientTherapyId.toString(),
          },
        }
      )
      .pipe(map((response) => response.responseData));
  }
  /**
   * Get Medical Diognosis and Treatment Diagnosis in the service
   * @param patientTherapyId 
   * @param url 
   * @returns 
   */
  getSectionGridData(patientTherapyId: number, url: string) {
    return this.httpClient.get<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      {
        params: {
          patientTherapyId: patientTherapyId.toString(),

        },
      }
    )
      .pipe(map((response) => response.responseData));
  }

  /**
* Get section tool tip for the given section
* @param patientTherapyId 
* @param url 
* @returns 
*/
  getSectionToolTip(params: any, url: string) {
    return this.httpClient.post<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      {
        ...params
      }
    )
      .pipe(map((response) => response.responseData));
  }

  /**
   * Get Mediacal ERM Diagnosis
   * @param patientTherapyId 
   * @returns
   */
  public getMediacalERMDiagnosis(patientFacilityId: number) {
    return this.httpClient.get<{ responseData: any }>(
      `${environment.endpoint}/lookup/get-patient-emr-diagnosis`,
      {
        params: {
          patientFacilityId: patientFacilityId
        },
      }
    )
      .pipe(map((response) => response.responseData));
  }

  /**
 * Get Mediacal ICD Diagnosis
 * @param facilityId
 * @param disciplineId
 * @param keyword
 * @returns
 */
  public getMediacalICDDiagnosis(facilityId: number, disciplineId: number, keyword: string) {
    return this.httpClient.post<{ responseData: any }>(
      `${environment.endpoint}/lookup/get-diagnosis-icd-codes`,
      {
        params: {
          facilityId: facilityId,
          disciplineId: disciplineId,
          keyword: keyword
        },
      }
    )
      .pipe(map((response) => response.responseData));
  }

  /**
   * updateMedicalDiagnosis
   * @param url 
   * @param patientTherapyId 
   * @param medicalDiagnosis 
   * @param treatmentDiagnosis 
   * @param medicalDiagnosisRequired 
   * @param treatmentDiagnosisRequired 
   * @returns 
   */
  public updateMedicalANDTreatmentDiagnosis(url: string, patientTherapyId: number, medicalDiagnosis: any,
    treatmentDiagnosis: any, medicalDiagnosisRequired: number, treatmentDiagnosisRequired: number, isTempSave?: boolean) {

    return this.httpClient.put<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      {
        patientTherapyId: patientTherapyId,
        medicalDiagnosis: medicalDiagnosis,
        treatmentDiagnosis: treatmentDiagnosis,
        medicalDiagnosisRequired: medicalDiagnosisRequired,
        treatmentDiagnosisRequired: treatmentDiagnosisRequired,
        isTempSave: isTempSave
      }
    )
      .pipe(map((response) => response.responseData));
  }

  public updateMedicalDiagnosisInTreatmentPlan(url: string, patientTherapyId: number, medicalDiagnosis: any, isTempSave?: boolean){
    return this.httpClient.put<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      {
        patientTherapyId: patientTherapyId,
        medicalDiagnosis: medicalDiagnosis,
        isTempSave: isTempSave,
        MinimumDiagnosisRequired: 1
      }
    ).pipe(map((response) => response.responseData));
  }

  /**
   * Get Common Diagnosis
   * @param disciplineId 
   * @param keyword 
   * @returns 
   */
  getCommonDiagnosisData(disciplineId: number, keyword: string) {
    return this.httpClient.post<{ responseData: any }>(
      `${environment.endpoint}/${this.endpoint}/get-common-diagnosis-codes`,
      {
        disciplineId: disciplineId,
        keyword: keyword
      }
    )
      .pipe(map((response) => response.responseData));
  }

  /**
   * api call for medical diagnosis history
   * @param patientTherapyId 
   * @returns 
   */
  public getMedicalDiagnosisHistory(patientTherapyId: number) {
    return this.httpClient.get<{ responseData: any }>(`${environment.endpoint}/patient/get-medical-diagnosis-history`,
      {
        params: {
          patientTherapyId: patientTherapyId.toString(),

        },
      }
    ).pipe(map((response) => response.responseData));
  }

  /**
 * Get Common CPT codes
 * @param disciplineId 
 * @param isEvalOnly
 * @param facilityId
 * @param keyword 
 * @returns 
 */
  getCommonCPTCodesData(disciplineId: number, isEvalOnly: boolean, facilityId: number, keyword: string) {
    return this.httpClient.get<{ responseData: any }>(
      `${environment.endpoint}/lookup/get-common-cpt-codes`,
      {
        params: {
          disciplineId: disciplineId,
          isEvalOnly: isEvalOnly,
          facilityId: facilityId,
          keyword: keyword
        },
      }
    )
      .pipe(map((response) => response.responseData));
  }

  /**
 * Get Other CPT codes
 * @param facilityId 
 * @param isEvalOnly
 * @param keyword 
 * @returns 
 */
  getOtherCPTCodesData(facilityId: number, isEvalOnly: boolean, keyword: string) {
    return this.httpClient.get<{ responseData: any }>(
      `${environment.endpoint}/lookup/get-other-cpt-codes`,
      {
        params: {
          facilityId: facilityId,
          isEvalOnly: isEvalOnly,
          keyword: keyword
        },
      }
    )
      .pipe(map((response) => response.responseData));
  }


  /**
   * updateMedicalDiagnosis
   * @param url 
   * @param parama
   * @returns 
   */
  public updateTreatmentPlan(url: string, params: any) {

    return this.httpClient.post<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      params
    )
      .pipe(map((response) => response.responseData));
  }

  /**
   * Makes API call to generate the AI response
   * @param response
   * @returns 
   */
  getAIGeneratedSummary(response: any, url: string) {
    return this.httpClient.post<{ responseData: any }>(`${environment.endpoint}/` + url,
      response,
    ).pipe(map((response) => response.responseData));
  }

  /**
   * puts the api call for changed co-signature
   * @param patientDocumentId 
   * @param facilityId 
   * @returns 
   */
  getDocumentCoSignature(patientDocumentId: number, facilityId: number) {
    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/get-document-co-signatures`,
        {
          params: {
            patientDocumentId: patientDocumentId,
            facilityid: facilityId,
          },
        }
      )
      .pipe(map((response) => response.responseData));
  }

  addOrRemoveCoSignature(response: { facilityId: number; patientDocumentId: number; isPerformingSignature: boolean; }) {
    return this.httpClient.put(
      `${environment.endpoint}/${this.endpoint}/perform-or-remove-co-signature`, response
    );
  }

  getDocumentTypes() {
    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/get-document-types`
      )
      .pipe(map((response) => response.responseData));
  }

  addPatientDocument(params: any) {
    return this.httpClient
      .post<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/add-patient-document`,
        params
      )
      .pipe(map((response) => response.responseData));
  }

  getActivityDateRange(patientDocumentId: number, patientTherapyId: any,  isDateForGrid: boolean, documentTypeId: number){

    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/get-activity-date-range`,
        {
          params: {
            patientDocumentId: patientDocumentId,
            patientTherapyId: patientTherapyId,
            isDateForGrid: isDateForGrid,
            documentTypeId: documentTypeId,
          },
        }
      )
      .pipe(map((response) => response.responseData));
    
  }

  getLastTherapyBillingDate(patientTherapyId: number) {
    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/get-last-therapy-billing-date`,
        {
          params: {
            patientTherapyId: patientTherapyId,
          },
        }
      )
      .pipe(map((response) => response.responseData));
  }

  checkIsDocumentComplete(patientTherapyId: number, verifyAllDocuments: boolean){
    return this.httpClient
      .get<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/check-completed-document`,
        {
          params: {
            patientTherapyId: patientTherapyId,
            verifyAllDocuments: verifyAllDocuments
          },
        }
      )
      .pipe(map((response) => response.responseData));
  }

  /**
   *
   * @param url 
   * @param parama
   * @returns 
   */
  public dynamicPUTCall(url: string, params: any) {

    return this.httpClient.put<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      params
    )
      .pipe(map((response) => response.responseData));
  }

  /**
   * getDateFormat
   */
  public getDateFormat(value: string) {
    if (value == EmptyData) {
      return value;
    }
    else if(value?.toString().includes(' - ')){
      const splitDates = value.split(' - ')
      return `${format(new Date(splitDates[0]), "MMM dd, yyyy")} - ${format(new Date(splitDates[1]), "MMM dd, yyyy")}`
    }
    if(isValid(parseISO(value))) {
      return format(parseISO(value), "MMM dd, yyyy");
    } else {
      return format(new Date(value), "MMM dd, yyyy");
    }
  }

  /**
   *
   * @param url 
   * @param parama
   * @returns 
   */
  public dynamicAPICall(url: string, params: any, method: string) {

    if(method == "PUT"){
      return this.httpClient.put<{ responseCode: string; responseData: any }>(
        `${environment.endpoint}/${url}`,
        params
      )
        .pipe(map((response) => {
          if(response.responseCode == "200" 
          && response.responseData == null 
          && url.includes('UpdateDocumentConfig')) {
            return true
          } 
          return response.responseData;
        }));
    }
    
    if(method == "POST"){
      return this.httpClient.post<{ responseData: any }>(
        `${environment.endpoint}/${url}`,
        params
      )
        .pipe(map((response) => response.responseData));
    }
    return this.httpClient.get<{ responseData: any }>(
      `${environment.endpoint}/${url}`,
      {
        params: params
      }
    ).pipe(map((response) => response.responseData));
  }

  copyHistoryPrecautions(
    PatientDocumentId: number
  ): Observable<any> {
    return this.httpClient
      .get<{ responseData: SourceData[] }>(
        `${environment.endpoint}/${this.endpoint}/get-selected-precautions-history`,
        {
          params: {
            patientDocumentId: PatientDocumentId
          },
        }
      )
      ?.pipe(map((response) => response.responseData));
  }
  getRecentPrecautions(
    PatientDocumentId: number
  ): Observable<any> {
    return this.httpClient
      .get<{ responseData: SourceData[] }>(
        `${environment.endpoint}/${this.endpoint}/get-precautions-from-recent-document`,
        {
          params: {
            patientDocumentId: PatientDocumentId
          },
        }
      )
      ?.pipe(map((response) => response.responseData));
  }

  /**
   *
   * @param patientDocumentId - The unique identifier of the patient's document to be signed.
   * @returns An Observable containing the response data as an array of `SourceData`.
   *
   */
  addeSignatureDocument(patientDocumentId: number, practitionerId: number): Observable<any> {
    return this.httpClient
      .post<{ response: any }>(
        `${environment.endpoint}/${this.endpoint}/add-ESignature-Document`,
        {
          patientDocumentId: patientDocumentId,
          practitionerId: practitionerId
        }
      )
      ?.pipe(map((response) => response));
  }
  /**
   *
   * @param patientDocumentId - The unique identifier of the patient's document to be signed.
   * @returns An Observable containing the response data as an array of `SourceData`.
   *
   */
  UpdateLockedeStatusESignatureDocument(patientDocumentId: number): Observable<any> {
    return this.httpClient
      .put<{ response: any }>(
        `${environment.endpoint}/${this.endpoint}/update-LockedStatus-eSignatureDocument?patientDocumentId=${patientDocumentId}`,
        {}
      )
      ?.pipe(map((response) => response));
  }

  GetPatientDocumentDetails(patientDocumentId: number): Observable<any> {
    return this.httpClient
      .get<Observable<any>>(
        `${environment.endpoint}/${this.endpoint}/get-patient-document-details/${patientDocumentId}`)
      ?.pipe(map((response) => response));
  }

  hasAccessToViewDeletedRecords(){
    return this.httpClient.get<{responseData : boolean}>(
      `${environment.endpoint}/${this.endpoint}/has-access-to-view-deleted-records`,
    ).pipe(map((response) => response.responseData));
  }

  getCanDeleteOptionBeEnabled(patienttherapyId : number){
    return this.httpClient.get<{responseData : DocumentDeletionConfirmationResponse }>(
      `${environment.endpoint}/${this.endpoint}/can-delete-option-be-enabled`,
      {
        params : {patienttherapyId}
      }
    ).pipe(map((response) => response.responseData));
  }

  putDeleteDocument(params: any) {
    return this.httpClient.put<{ responseData: any }>(
      `${environment.endpoint}/${this.endpoint}/delete-document`,
      {
        ...params
      }
    ).pipe(map((response) => response.responseData));
  }

  hasAbilityToEditDoc(patientTherapyId : string){
    return this.httpClient.get<{responseData : boolean}>(
      `${environment.endpoint}/${this.endpoint}/has-ability-to-edit-document`,
      {
        params : {patientTherapyId}
      }
    ).pipe(map((response) => response.responseData));
  }

  hasNextDocumentsInitiated(patientTherapyId : string, activityDate: string){
    return this.httpClient.get<{responseData : boolean}>(
      `${environment.endpoint}/${this.endpoint}/has-next-documents-initiated`,
      {
        params : {
          patientTherapyId: patientTherapyId, 
          activityDate: activityDate
        }
      }
    ).pipe(map((response) => response.responseData));
  }

  EvaluationOnlyCheckRequest(patientDocumentId: number, evalOnly: boolean){
    return this.httpClient
      .post<{ responseData: boolean }>(
        `${environment.endpoint}/${this.endpoint}/evaluation-only-check`,
        {
          patientDocumentId: patientDocumentId,
          evaluationOnly: evalOnly
        }
      )
      ?.pipe(map((response) => response?.responseData));
  }
  checkTransitionalCSVEPOCDoc(patientDocumentId: number) {
    return this.httpClient
      .get<{ responseData: boolean }>(
        `${environment.endpoint}/${this.endpoint}/check-transitional-CSV-EPOC-doc`,
        {
          params: { patientDocumentId },
        }
      )
      .pipe(map((response) => response.responseData));
  }
  /**
  *
  * @param payload 
  * @returns An Observable containing the response data.
  *
  */
  documentRefetch(payload: any): Observable<any> {
    return this.httpClient
      .post<{ responseData: any }>(
        `${environment.endpoint}/${this.endpoint}/refetch-document`,
        {
          ...payload
        }
      )
      ?.pipe(map((response) => response.responseData));
  }

  checkDCSaveCondition(patientDocumentId: number): Observable<any> {
    return this.httpClient
      .get<{ response: any }>(
        `${environment.endpoint}/${this.endpoint}/can-save-dc-document?patientDocumentId=${patientDocumentId}`)
      ?.pipe(map((response) => response));
  }
  
  hasNextTherapyEpisode(patientTherapyId : string){
    return this.httpClient.get<{responseData : boolean}>(
      `${environment.endpoint}/${this.endpoint}/has-next-therapy-episode`,
      {
        params : {patientTherapyId}
      }
    ).pipe(map((response) => response.responseData));
  }

  toLocalDate(value: string | Date | null | undefined): Date | null {
    if (!value) return null;
  
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      return new Date(year, month - 1, day, 0, 0, 0); 
    }
  
    if (typeof value === "string" && /^\d{2}\/\d{2}\/\d{4}$/.test(value)) {
      const [month, day, year] = value.split("/").map(Number);
      return new Date(year, month - 1, day, 0, 0, 0);
    }
  
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0);
  }

}
